import graphqlHelperService from "./graphqlHelper.service";
import { Registration, RegistrationPriceInfo, RegisterKHN, RegisterResponse, RegisterPriceResponse, ResponseReason } from "../types/register";
import { capitalizeFirstLetter } from "../helperfunctions/data";
import { CheckoutPaymentFormData } from "../components/Checkout/CheckoutPayment";

export const registerService = {
    register: async (shoppingCart: Registration[], paymentData: CheckoutPaymentFormData): Promise<RegisterResponse> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            registerMutation,
            { registration: buildRegistrationInput(shoppingCart, paymentData) }
        );
        return apolloResult.register as RegisterResponse;
    },
    registerInformationKHN: async (khnId: number, zipCode: string): Promise<RegisterKHN> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            registerInformationKHN(khnId, zipCode)
        );
        return apolloResult.dataByKhn as RegisterKHN;
    },
    uploadKHNData: async (content: string): Promise<boolean> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            importKhnData(content)
        );
        return apolloResult.importKhnData as boolean;
    },
    uploadAgreement: async (content: string, fileName: string): Promise<boolean> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            uploadAgreementQuery(content, fileName)
        );
        return apolloResult.setUserAgreement as boolean;
    },
    getUserAgreementFileName: async (): Promise<string> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            getUserAgreementFileNameQuery()
        );
        return apolloResult.getUserAgreementFileName as string;
    },
    calculatePrice: async (priceItems: RegistrationPriceInfo[]): Promise<RegisterPriceResponse[]> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            calculatePriceMutation,
            { priceItems: buildPriceItemsInput(priceItems) }
        );
        return apolloResult.calculatePrices as RegisterPriceResponse[];
    },
    getAgreement: async (): Promise<string> => {
        const apolloResult = await graphqlHelperService.sendMutate(
            downloadAgreementQuery()
        );
        return apolloResult.getUserAgreement as string;
    },
    getResponseReasons: async (): Promise<ResponseReason[]> => {
        const apolloResult = await graphqlHelperService.sendQuery(
            responseReasonsQuery()
        );
        return apolloResult.responseReasons as ResponseReason[];
    },
};

const responseReasonsQuery = () => `query {
    responseReasons {
        code
        name
    }
}`

const getUserAgreementFileNameQuery = () => `query {
    getUserAgreementFileName
}`

const downloadAgreementQuery = () => `query {
    getUserAgreement
}`

const uploadAgreementQuery = (content: string, fileName: string) => `mutation {
    setUserAgreement(file: "${content}" fileName: "${fileName}")
}`

const importKhnData = (content: string) => `mutation {
    importKhnData(file: "${content}")
}`

const registerInformationKHN = (khnId: number, zipCode: string) => `query {
    dataByKhn(khnId: ${khnId}, zipCode: "${zipCode}") {
        nameCompany
        emailCompany
        zipCodeCompany
        houseNumberCompany
        placeCompany
        adresCompany
        phoneCompany
        phoneMobileContact
        initialsContact
        infixContact
        firstNameContact
        lastNameContact
        emailContact
        byMail
        adresInvoice
        numberInvoice
        zipCodeInvoice
        placeInvoice
        emailInvoice
    }
}`

// Parameterized operation: all user input travels as a GraphQL variable (JSON),
// so values containing quotes/newlines can no longer break the query document.
const registerMutation = `mutation Register($registration: RegistrationInput!) {
    register(registration: $registration) {
        successful
        idealLink
    }
}`;

// Returns undefined for empty/falsy strings so the field is omitted from the JSON
// (mirrors the old `value ? ... : ``` conditionals).
const opt = (value?: string): string | undefined => value ? value : undefined;

// Builds the RegistrationInput object sent as the `$registration` variable.
const buildRegistrationInput = (shoppingCart: Registration[], paymentData: CheckoutPaymentFormData) => ({
    shoppingCart: (shoppingCart || []).map((registration) => ({
        course: {
            courseId: registration.course.type?.courseId,
            paymentMethod: paymentData.paymentMethod,
            locationCode: opt(registration.course.date?.locationCode),
        },
        relation: registration.relation ? {
            contact: {
                sex: registration.relation.contact.sex,
                initials: registration.relation.contact.initials ? registration.relation.contact.initials.toUpperCase() : undefined,
                prefix: opt(registration.relation.contact.prefix),
                firstName: capitalizeFirstLetter(registration.relation.contact.firstName),
                lastName: capitalizeFirstLetter(registration.relation.contact.lastName),
                birthDate: opt(registration.relation.contact.birthDate),
                birthPlace: opt(registration.relation.contact.birthPlace),
                email: opt(registration.relation.contact.email),
            },
            invoice: registration.relation.invoice ? {
                email: opt(registration.relation.invoice.email),
                byMail: !!registration.relation.invoice.byMail,
                ...(registration.relation.invoice.byMail ? {
                    address: capitalizeFirstLetter(registration.relation.invoice.address),
                    houseNumber: registration.relation.invoice.houseNumber,
                    zipCode: registration.relation.invoice.zipCode,
                    place: capitalizeFirstLetter(registration.relation.invoice.place),
                } : {}),
            } : undefined,
            companyName: opt(registration.relation.companyName),
            zipCode: registration.relation.zipCode,
            houseNumber: registration.relation.houseNumber,
            address: capitalizeFirstLetter(registration.relation.address),
            place: capitalizeFirstLetter(registration.relation.place),
            workNumber: opt(registration.relation.workNumber),
            mobileNumber: opt(registration.relation.mobileNumber),
            email: registration.relation.email,
            password: opt(registration.relation.password),
            studentId: opt(registration.relation.studentId),
            autoFilled: registration.relation.autoFilled ? registration.relation.autoFilled : undefined,
        } : undefined,
        students: registration.students ? registration.students.map((student) => ({
            studentId: opt(student.studentId),
            birthDate: opt(student.birthDate),
            birthPlace: opt(student.birthPlace),
            place: capitalizeFirstLetter(student.place),
            sex: opt(student.sex),
            initials: student.initials ? student.initials.toUpperCase() : undefined,
            firstName: capitalizeFirstLetter(student.firstName),
            lastName: capitalizeFirstLetter(student.lastName),
            prefix: opt(student.prefix),
            address: capitalizeFirstLetter(student.address),
            houseNumber: opt(student.houseNumber),
            zipCode: opt(student.zipCode),
            privateNumber: opt(student.privateNumber),
            mobileNumber: opt(student.mobileNumber),
            email: opt(student.email),
            books: !!student.books,
        })) : undefined,
        freelancerBooks: registration.freelancerBooks ? !!registration.freelancerBooks : undefined,
    })),
    discountCode: opt(paymentData.discountCode),
    note: opt(paymentData.note),
    remark: opt(paymentData.remark),
    paymentFeature: opt(paymentData.paymentFeature),
    issuerId: opt(paymentData.issuerId),
    paymentMethod: paymentData.paymentMethod,
    khnId: opt(paymentData.khnId),
});

const calculatePriceMutation = `mutation CalculatePrices($priceItems: [RegistrationPriceInput]!) {
    calculatePrices(priceItems: $priceItems) {
        price
        discountInfo
        portalPaymentMethod
        khnDiscount
    }
}`;

const buildPriceItemsInput = (priceItems: RegistrationPriceInfo[]) =>
    (priceItems || []).map((item) => ({
        courseId: item.courseId,
        studentBooks: item.studentBooks,
        discountCode: opt(item.discountCode),
        khnId: opt(item.khnId),
    }));